import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FlaskConical, Plus, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResearchPage = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'hr', 'manager'].includes(user?.role);
  const [research, setResearch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [newResearch, setNewResearch] = useState({
    title: '',
    type: '',
    status: 'draft',
    conference: '',
    co_authors: '',
    url: ''
  });

  const fetchResearch = useCallback(async () => {
    try {
      let url = `${API_URL}/api/research`;
      if (typeFilter !== 'all') url += `?type=${typeFilter}`;
      const response = await axios.get(url, { withCredentials: true });
      setResearch(response.data);
    } catch (error) {
      console.error('Fetch research error:', error);
      toast.error('Failed to load research');
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    fetchResearch();
  }, [fetchResearch]);

  const handleAddResearch = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/research`, {
        ...newResearch,
        employee_id: '', // Will be set by backend
        co_authors: newResearch.co_authors.split(',').map(a => a.trim()).filter(Boolean)
      }, { withCredentials: true });
      toast.success('Research added');
      setIsAddDialogOpen(false);
      setNewResearch({ title: '', type: '', status: 'draft', conference: '', co_authors: '', url: '' });
      fetchResearch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add research');
    }
  };

  const getTypeBadge = (type) => {
    const classes = { paper: 'badge-ai', patent: 'badge-success', conference: 'badge-warning' };
    return <Badge className={classes[type]}>{type}</Badge>;
  };

  const getStatusBadge = (status) => {
    const classes = { draft: 'bg-muted', submitted: 'badge-warning', accepted: 'badge-success', published: 'badge-ai', filed: 'badge-warning', granted: 'badge-success' };
    return <Badge className={classes[status]}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="research-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FlaskConical className="w-7 h-7 text-primary" />
            Research & Patents
          </h1>
          <p className="text-muted-foreground">Track publications, conferences, and IP filings</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-research-btn"><Plus className="w-4 h-4" />Add Research</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Research/Patent</DialogTitle></DialogHeader>
            <form onSubmit={handleAddResearch} className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={newResearch.title} onChange={(e) => setNewResearch({ ...newResearch, title: e.target.value })} required data-testid="research-title" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={newResearch.type} onValueChange={(v) => setNewResearch({ ...newResearch, type: v })}>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paper">Paper</SelectItem>
                      <SelectItem value="patent">Patent</SelectItem>
                      <SelectItem value="conference">Conference</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={newResearch.status} onValueChange={(v) => setNewResearch({ ...newResearch, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="filed">Filed</SelectItem>
                      <SelectItem value="granted">Granted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Conference/Journal</Label>
                <Input value={newResearch.conference} onChange={(e) => setNewResearch({ ...newResearch, conference: e.target.value })} placeholder="e.g., NeurIPS, CVPR" />
              </div>
              <div className="space-y-2">
                <Label>Co-Authors (comma separated)</Label>
                <Input value={newResearch.co_authors} onChange={(e) => setNewResearch({ ...newResearch, co_authors: e.target.value })} placeholder="Name 1, Name 2" />
              </div>
              <div className="space-y-2">
                <Label>URL</Label>
                <Input value={newResearch.url} onChange={(e) => setNewResearch({ ...newResearch, url: e.target.value })} placeholder="https://..." />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-research">Add</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Papers</p><p className="text-2xl font-bold">{research.filter(r => r.type === 'paper').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Patents</p><p className="text-2xl font-bold">{research.filter(r => r.type === 'patent').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Conferences</p><p className="text-2xl font-bold">{research.filter(r => r.type === 'conference').length}</p></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Filter by type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="paper">Papers</SelectItem>
            <SelectItem value="patent">Patents</SelectItem>
            <SelectItem value="conference">Conferences</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Research List */}
      <div className="space-y-4">
        {research.map((item) => (
          <Card key={item.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    {getTypeBadge(item.type)}
                    {getStatusBadge(item.status)}
                  </div>
                  <h3 className="font-semibold">{item.title}</h3>
                  {isAdmin && item.employee_name && (
                    <p className="text-sm text-primary font-medium mt-0.5">By: {item.employee_name}</p>
                  )}
                  {item.conference && <p className="text-sm text-muted-foreground mt-1">{item.conference}</p>}
                  {item.co_authors?.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">Co-authors: {item.co_authors.join(', ')}</p>
                  )}
                </div>
                {item.url && (
                  <Button variant="outline" size="sm" onClick={() => window.open(item.url, '_blank')}>
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {research.length === 0 && (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No research or patents found</CardContent></Card>
      )}
    </div>
  );
};

export default ResearchPage;
